import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import {mapValues} from 'common/util';
import {debug} from 'common/report/util';
import {Clause, FilterClause} from 'common/report/insights/models/clause';
import {
  CohortsSection,
  FilterSection,
  FormulaSection,
  GroupSection,
  Section,
  ShowSection,
  TimeSection,
} from 'common/report/insights/models/section';

type BuilderSectionsTypes = `show` | `cohorts` | `group` | `filter` | `formula` | `time`;
const BuilderSectionsTypesArray: BuilderSectionsTypes[] = [`show`, `cohorts`, `group`, `filter`, `formula`, `time`];

export class BuilderSections {
  show: ShowSection;
  cohorts: CohortsSection;
  group: GroupSection;
  filter: FilterSection;
  formula: FormulaSection;
  time: TimeSection;

  globalDataGroupId: string;

  static TYPES = BuilderSectionsTypesArray;

  constructor(attrs: TODO = {}) {
    this.show = attrs.show || new ShowSection();
    this.cohorts = attrs.cohorts || new CohortsSection();
    this.group = attrs.group || new GroupSection();
    this.filter = attrs.filter || new FilterSection();
    this.formula = attrs.formula || new FormulaSection();
    this.time = attrs.time || new TimeSection();

    // Insights API requires a data group ID for each clause in each section,
    // even though it will always be one value in the current UI.
    // Setting this global while applying dataGroupId to each clause will allow us to statefully persist
    // dataGroupId in a clean (new) report.
    this.globalDataGroupId = attrs.globalDataGroupId;
  }

  static deserialize(attrs, syncCustomEvent = null) {
    // @TODO jun Hack - after p3 rollout, think of a better way of handling this case
    // when we are loading a report that is saved w/ value comparison (e.g. group_by section),
    // apply transformation for Builder Sections
    if (attrs.group_by) {
      attrs.group = attrs.group_by;
    }

    const sections = pick(attrs, BuilderSections.TYPES);
    return new BuilderSections({
      ...attrs,
      ...mapValues(sections, (clauseAttrs, sectionType) =>
        // pass down custom event sync function so clauses can get the latest version of each custom event
        Section.deserialize(sectionType, clauseAttrs, syncCustomEvent),
      ),
    });
  }

  get attrs() {
    return pick(this, BuilderSections.TYPES);
  }

  getClause(type, clauseIndex) {
    return this[type].clauses[clauseIndex];
  }

  replaceSection(section) {
    const newAttrs = {
      ...this.attrs,
      [section.TYPE]: section,
      globalDataGroupId: this.globalDataGroupId,
    };

    return this.validate(new BuilderSections(newAttrs));
  }

  removeEmptySections() {
    return this.validate(new BuilderSections(pickBy(this.attrs, (section) => section.hasClauses())));
  }

  mapNonEmptySections(mapFunc) {
    // Currently only toUrlData uses this function. The other serialization functions can be updated to this
    // to use this once it's been validated there's no code that depends on empty sections.
    return mapValues(
      pickBy(this.attrs, (section) => section.hasClauses()),
      mapFunc,
    );
  }

  serialize() {
    return {
      ...mapValues(this.attrs, (section) => section.serialize()),
      ...(this.globalDataGroupId ? {globalDataGroupId: this.globalDataGroupId} : null),
    };
  }

  toBookmarkData() {
    return {
      ...mapValues(this.attrs, (section) => section.toBookmarkData()),
      ...(this.globalDataGroupId ? {globalDataGroupId: this.globalDataGroupId} : null),
    };
  }

  toUrlData() {
    return {
      ...this.mapNonEmptySections((section) => section.toUrlData()),
      ...(this.globalDataGroupId ? {globalDataGroupId: this.globalDataGroupId} : null),
    };
  }

  get valid() {
    return (
      this.show instanceof ShowSection &&
      this.show.valid &&
      this.cohorts instanceof CohortsSection &&
      this.cohorts.valid &&
      this.group instanceof GroupSection &&
      this.group.valid &&
      this.filter instanceof FilterSection &&
      this.filter.valid &&
      this.formula instanceof FormulaSection &&
      this.formula.valid &&
      this.time instanceof TimeSection &&
      this.time.valid
    );
  }

  removeNewQueryBuilderFeatures() {
    const excludeInlineCohorts = (clause) => !clause.isInlineCohort;
    return this.replaceSection(this.show.removeEventFilters().filterClauses(excludeInlineCohorts))
      .replaceSection(this.filter.removeMultipleCohortFilters().filterClauses(excludeInlineCohorts))
      .replaceSection(this.cohorts.filterClauses(excludeInlineCohorts))
      .removeEmptySections();
  }

  setDataGroupId({dataGroupId, customEvents}) {
    this.globalDataGroupId = dataGroupId;

    return this.replaceSection(this.show.setDataGroupId(dataGroupId).makeValidForDataGroup(dataGroupId, customEvents))
      .replaceSection(
        this.group
          .setDataGroupIdByResourceType(dataGroupId, Clause.RESOURCE_TYPE_EVENTS)
          .removeUserClauses(dataGroupId),
      )
      .replaceSection(this.cohorts.makeValidForDataGroup(dataGroupId))
      .replaceSection(
        this.filter
          .setDataGroupIdByResourceType(dataGroupId, Clause.RESOURCE_TYPE_EVENTS)
          .makeValidForDataGroup(dataGroupId),
      );
  }

  getDataGroupId() {
    const {clauses} = this.show;
    const clauseGroupId = clauses.length ? clauses[0].dataGroupId : null;
    return this.globalDataGroupId ?? clauseGroupId;
  }

  listNewQueryBuilderFeatures() {
    const {hasPerEventFilters} = this.show;
    const hasMultipleCohortFilters = this.filter.clauses
      .filter((clause) => clause.value === FilterClause.COHORTS)
      .some((clause) => clause.filterValue.length > 1);
    const hasInlineCohorts = [
      ...(this.filter.clauses || []),
      ...(this.cohorts.clauses || []),
      ...(this.show.clauses || []),
    ].some((clause) => clause.isInlineCohort);

    return {
      hasPerEventFilters,
      hasMultipleCohortFilters,
      hasInlineCohorts,
    };
  }

  hasNewQueryBuilderFeatures() {
    return Object.values(this.listNewQueryBuilderFeatures()).some(Boolean);
  }

  hasUserParamsInvalidForDataGroups(customEvents) {
    return (
      this.show.hasClausesInvalidForDataGroups(customEvents) ||
      this.group.hasUserClauses() ||
      this.cohorts.hasClausesInvalidForDataGroups() ||
      this.filter.hasClausesInvalidForDataGroups()
    );
  }

  validate(newSections) {
    const valid = newSections.valid;

    if (!valid) {
      debug.warn(`sections invalid:`, newSections);
    }

    return valid ? newSections : this;
  }
}
